import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { Users, Phone, Receipt, Clock, TrendingUp, Activity, Server, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react'
import { CurrencyDisplay } from '@/components/ui/currency-display'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { ProfitLossPeriod, ProfitLossTrendDay, ProfitLossTrendResponse, RevenueTrendDay } from '@/services/adminApi'
import { ComposedChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { useCurrency } from '@/hooks/useCurrency'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboardPage,
})

const periodOptions: { value: ProfitLossPeriod; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
]

function RevenueSparkline({ trend, isLoading }: { trend: RevenueTrendDay[] | undefined; isLoading: boolean }) {
  const { formatCurrency } = useCurrency()
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50px]">
        <Skeleton className="h-[30px] w-[100px]" />
      </div>
    )
  }

  if (!trend || trend.length === 0) {
    return (
      <div className="flex items-center justify-center h-[50px] text-xs text-muted-foreground">
        No data
      </div>
    )
  }

  const chartData = trend.map(d => ({ v: d.totalRevenue / 100 }))
  const totalRevenue = chartData.reduce((a, b) => a + b.v, 0)

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-xs text-muted-foreground">7d Total</p>
        <p className="text-lg font-bold">{formatCurrency(totalRevenue)}</p>
      </div>
      <ResponsiveContainer width="100%" height={40}>
        <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <Line type="monotone" dataKey="v" stroke="#22c55e" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function ProfitLossTrendChart({ data, isLoading }: { data: ProfitLossTrendResponse | undefined; isLoading: boolean }) {
  const { formatCurrency } = useCurrency()
  if (isLoading) {
    return <Skeleton className="h-[200px] w-full" />
  }

  if (!data || data.trend.length === 0) {
    return <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">No trend data</div>
  }

  const daysInPeriod = data.trend.length
  const dailyBreakevenGbp = data.breakevenMonthlyCents / 100 / (daysInPeriod / 30)

  const chartData = data.trend.map((day: ProfitLossTrendDay) => ({
    date: day.date.slice(5),
    revenue: +(day.totalRevenue / 100).toFixed(2),
    topup: +(day.topupRevenue / 100).toFixed(2),
    calls: +(day.callRevenue / 100).toFixed(2),
  }))

  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11 }}
          interval={Math.floor(daysInPeriod / 6)}
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => formatCurrency(v as number)}
          width={48}
        />
        <Tooltip
          formatter={(value) => [formatCurrency(value as number)]}
        />
        <Bar dataKey="revenue" fill="#22c55e" name="Revenue" radius={[2, 2, 0, 0]} />
        {dailyBreakevenGbp > 0 && (
          <ReferenceLine
            y={dailyBreakevenGbp}
            stroke="#ef4444"
            strokeDasharray="4 4"
            label={{ value: 'Breakeven', position: 'insideTopRight', fontSize: 11, fill: '#ef4444' }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

function AdminDashboardPage() {
  const { data: stats, isLoading } = useQuery(adminQueries.stats())
  const [plPeriod, setPlPeriod] = useState<ProfitLossPeriod>('30d')
  const { data: pl, isLoading: plLoading } = useQuery(adminQueries.profitLoss(plPeriod))
  const { data: plTrend, isLoading: plTrendLoading } = useQuery(adminQueries.profitLossTrend(plPeriod))
  const { formatCurrency } = useCurrency()

  const formatLastSync = (isoString: string | null | undefined) => {
    if (!isoString) return 'Never'
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Dashboard</h1>

      {/* Top Row: Key Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        {/* Accounts */}
        <Link to="/admin/users" className="contents">
          <Card className="hover:bg-muted/50 transition-colors py-4 gap-3">
            <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Accounts</CardTitle>
              <div className="rounded-md bg-blue-500/10 p-1.5 shrink-0">
                <Users className="h-3.5 w-3.5 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent className="px-4 space-y-0.5">
              {isLoading ? (
                <div className="space-y-1.5">
                  <Skeleton className="h-7 w-16" />
                  <Skeleton className="h-3.5 w-24" />
                </div>
              ) : (
                <>
                  <p className="text-2xl font-bold tracking-tight">{(stats?.totalAccounts ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{(stats?.activeAccounts ?? 0).toLocaleString()}</span> active this month
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Calls */}
        <Link to="/admin/calls" className="contents">
          <Card className="hover:bg-muted/50 transition-colors py-4 gap-3">
            <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Calls</CardTitle>
              <div className="rounded-md bg-green-500/10 p-1.5 shrink-0">
                <Phone className="h-3.5 w-3.5 text-green-500" />
              </div>
            </CardHeader>
            <CardContent className="px-4 space-y-0.5">
              {isLoading ? (
                <div className="space-y-1.5">
                  <Skeleton className="h-7 w-20" />
                  <Skeleton className="h-3.5 w-16" />
                </div>
              ) : (
                <>
                  <p className="text-2xl font-bold tracking-tight">{(stats?.totalCalls ?? 0).toLocaleString()}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                    </span>
                    <span className="text-xs text-muted-foreground">
                      <span className="font-semibold text-green-600 dark:text-green-400">{stats?.activeCalls ?? 0}</span> active now
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Transactions */}
        <Link to="/admin/transactions" className="contents">
          <Card className="hover:bg-muted/50 transition-colors py-4 gap-3">
            <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Transactions</CardTitle>
              <div className="rounded-md bg-violet-500/10 p-1.5 shrink-0">
                <Receipt className="h-3.5 w-3.5 text-violet-500" />
              </div>
            </CardHeader>
            <CardContent className="px-4 space-y-0.5">
              {isLoading ? (
                <div className="space-y-1.5">
                  <Skeleton className="h-7 w-16" />
                  <Skeleton className="h-3.5 w-24" />
                </div>
              ) : (
                <>
                  <p className="text-2xl font-bold tracking-tight">{(stats?.totalTransactions ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {formatCurrency((stats?.totalTopUpRevenue ?? 0) / 100)}
                    </span> topped up total
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Call Minutes */}
        <Card className="py-4 gap-3">
          <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Call Minutes</CardTitle>
            <div className="rounded-md bg-orange-500/10 p-1.5 shrink-0">
              <Clock className="h-3.5 w-3.5 text-orange-500" />
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-0.5">
            {isLoading ? (
              <div className="space-y-1.5">
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-3.5 w-24" />
              </div>
            ) : (
              <>
                <p className="text-2xl font-bold tracking-tight">{(stats?.totalCallMinutes ?? 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  avg{' '}
                  <span className="font-semibold text-foreground">
                    {stats?.totalCalls
                      ? ((stats.totalCallMinutes / stats.totalCalls) * 60).toFixed(0)
                      : '0'}s
                  </span>{' '}
                  per call
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Call Revenue */}
        <Card className="py-4 gap-3">
          <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Call Revenue</CardTitle>
            <div className="rounded-md bg-emerald-500/10 p-1.5 shrink-0">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-0.5">
            {isLoading ? (
              <div className="space-y-1.5">
                <Skeleton className="h-7 w-20" />
                <Skeleton className="h-3.5 w-24" />
              </div>
            ) : (
              <>
                <CurrencyDisplay
                  amount={(stats?.totalCallRevenue ?? 0) / 100}
                  className="text-2xl font-bold tracking-tight"
                  hideSign
                />
                <p className="text-xs text-muted-foreground">
                  avg{' '}
                  <span className="font-semibold text-foreground">
                    {stats?.totalCalls
                      ? formatCurrency((stats.totalCallRevenue / stats.totalCalls) / 100, { decimalPlaces: 3 })
                      : formatCurrency(0, { decimalPlaces: 3 })}
                  </span>{' '}
                  per call
                </p>
              </>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Second Row: Revenue Trend + Provider Breakdown + System Health */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Revenue Trend */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Revenue Trend (7d)
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <RevenueSparkline trend={stats?.revenueTrend} isLoading={isLoading} />
          </CardContent>
        </Card>

        {/* Provider Breakdown */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Provider Split (24h)
            </CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : stats?.providerBreakdown ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm font-medium">Twilio</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold">{stats.providerBreakdown.twilio.count}</span>
                    <span className="text-xs text-muted-foreground ml-1">({stats.providerBreakdown.twilio.percentage}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${stats.providerBreakdown.twilio.percentage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-sm font-medium">Telnyx</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold">{stats.providerBreakdown.telnyx.count}</span>
                    <span className="text-xs text-muted-foreground ml-1">({stats.providerBreakdown.telnyx.percentage}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${stats.providerBreakdown.telnyx.percentage}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Total: {stats.providerBreakdown.total} calls
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No provider data</p>
            )}
          </CardContent>
        </Card>

        {/* System Health */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              System Health
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : stats?.systemHealth ? (
              <div className="space-y-2">
                {/* Provider Sync Status */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Last Sync</span>
                  <div className="flex items-center gap-2">
                    <RefreshCw className={`h-3 w-3 ${stats.systemHealth.providerSync.flaggedRecords > 0 ? 'text-orange-500' : 'text-green-500'}`} />
                    <span className="text-sm">
                      {formatLastSync(stats.systemHealth.providerSync.lastSync)}
                    </span>
                  </div>
                </div>
                
                {/* Flagged Records */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Flagged Records</span>
                  {stats.systemHealth.providerSync.flaggedRecords > 0 ? (
                    <Link to="/admin/calls" className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 text-orange-500" />
                      <Badge variant="destructive" className="text-xs">
                        {stats.systemHealth.providerSync.flaggedRecords}
                      </Badge>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span className="text-sm text-green-600 dark:text-green-400">OK</span>
                    </div>
                  )}
                </div>

                {/* Scheduler Status */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Scheduler</span>
                  <div className="flex items-center gap-1">
                    {stats.systemHealth.scheduler.failedTasks.length > 0 ? (
                      <>
                        <AlertCircle className="h-3 w-3 text-red-500" />
                        <Badge variant="destructive" className="text-xs">
                          {stats.systemHealth.scheduler.failedTasks.length} failed
                        </Badge>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span className="text-sm text-green-600 dark:text-green-400">
                          {stats.systemHealth.scheduler.enabledTasks}/{stats.systemHealth.scheduler.totalTasks} enabled
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No health data</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Profit & Loss</CardTitle>
          <div className="flex gap-1">
            {periodOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={plPeriod === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPlPeriod(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {plLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : pl ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Billed</p>
                  <CurrencyDisplay amount={pl.billedCents / 100} className="text-2xl font-bold text-green-600 dark:text-green-400" hideSign />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Provider Cost</p>
                  <CurrencyDisplay amount={pl.providerCostCents / 100} className="text-2xl font-bold text-orange-600 dark:text-orange-400" hideSign />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gross Profit ({pl.grossMarginPct}%)</p>
                  <CurrencyDisplay
                    amount={pl.grossProfitCents / 100}
                    className={`text-2xl font-bold ${pl.grossProfitCents >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                    hideSign
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fixed Costs</p>
                  <CurrencyDisplay amount={pl.fixedCostsCents / 100} className="text-2xl font-bold text-orange-600 dark:text-orange-400" hideSign />
                   <p className="text-xs text-muted-foreground">
                     numbers: {formatCurrency(pl.numberCostsCents / 100)} + hosting: {formatCurrency((pl.fixedCostsCents - pl.numberCostsCents) / 100)}
                   </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Net Cash Flow</p>
                  <CurrencyDisplay
                    amount={pl.netCashFlowCents / 100}
                    className={`text-2xl font-bold ${pl.netCashFlowCents >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                    hideSign
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {pl.callCount} calls: {pl.matchedCalls} matched, {pl.estimatedCalls} estimated
              </p>
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-2">Daily revenue vs breakeven pace</p>
                <ProfitLossTrendChart data={plTrend} isLoading={plTrendLoading} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data available.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card flush className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-lg">Recent Calls</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : stats?.recentCalls?.length ? (
              <div className="max-sm:-mx-6 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Destination</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recentCalls.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell className="font-mono">{call.destinationNumber}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              call.status === 'completed'
                                ? 'default'
                                : call.status === 'failed'
                                  ? 'destructive'
                                  : 'secondary'
                            }
                          >
                            {call.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {call.duration != null ? (
                            <span>
                              {Math.floor(call.duration / 60)}:{String(call.duration % 60).padStart(2, '0')}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {call.cost ? (
                            <CurrencyDisplay amount={call.cost.amount} currency={call.cost.currency} debit hideSign hideColor inCents />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No calls yet.</p>
            )}
          </CardContent>
        </Card>

        <Card flush className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-lg">Recent Signups</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : stats?.recentSignups?.length ? (
              <div className="max-sm:-mx-6 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recentSignups.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.email}</TableCell>
                        <TableCell>
                          <Badge variant={account.role === 'admin' ? 'default' : 'secondary'}>
                            {account.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(account.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link
                            to="/admin/users/$accountId"
                            params={{ accountId: account.id }}
                            className="text-primary underline-offset-4 hover:underline"
                          >
                            View
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No signups yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
